import { BackgroundTaskType } from "@api/progress-api/progress-api-types";
import { SdbBackgroundTask } from "@custom-types/sdb-background-tasks-types";
import {
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  fetchTasksByType,
  updateBackgroundTasks,
} from "@store/sdb-background-tasks/sdb-background-tasks-thunk";
import {
  BaseEntityAdapter,
  BaseEntityState,
  FetchingStatus,
  StringEntityId,
} from "@store/store-types";

/**
 * State of the background tasks
 */
interface SdbBackgroundTasksState extends BaseEntityState<SdbBackgroundTask> {
  /** Pagination properties for this slice */
  pagination: {
    /** Current display page */
    page: number;

    /** Reference ID to the previous page. `null` if this is the first page */
    before: StringEntityId | null;

    /** Reference ID to the next page. `null` if this is the last page */
    after: StringEntityId | null;

    /** Total count of all background tasks. `null` if not known yet */
    totalCount: number | null;

    /**
     * Total count of background tasks by type.
     * This is set by fetchTasksByType when fetching tasks of a specific type
     * and allows each task type to maintain its own count independently.
     * This prevents conflicts when multiple hooks fetch different task types.
     */
    totalCountByType: Record<BackgroundTaskType, number | null>;
  };

  /** Fetching properties for this slice */
  fetching: {
    /** Indicates if background tasks are being fetched from the backend */
    backgroundTasksFetchingStatus: FetchingStatus;

    /**
     * Fetching status by task type.
     * This is set by fetchTasksByType and allows tracking the fetch status
     * for each task type independently, preventing race conditions when
     * multiple hooks fetch different task types simultaneously.
     */
    fetchingStatusByType: Record<BackgroundTaskType, FetchingStatus>;
  };
}

/** Creates an entity adapter to store a map with all the background tasks that the user has access to */
export const sdbBackgroundTasksAdapter: BaseEntityAdapter<SdbBackgroundTask> =
  createEntityAdapter({
    selectId: (sdbBackgroundTask) => sdbBackgroundTask.id,
  });

/** Initial SdbBackgroundTasksState state */
export const initialState: SdbBackgroundTasksState = {
  ...sdbBackgroundTasksAdapter.getInitialState(),
  pagination: {
    page: 0,
    before: null,
    after: null,
    totalCount: null,
    totalCountByType: {},
  },
  fetching: {
    backgroundTasksFetchingStatus: "uninitialized",
    fetchingStatusByType: {},
  },
};

/**
 * Slice to access the state of the the loaded background tasks
 */
const backgroundTasksSlice = createSlice({
  name: "sdbBackgroundTasks",
  initialState,
  reducers: {
    /**
     * Accepts a single sdbBackgroundTask entity and adds or updates it in the store.
     */
    upsertOne: sdbBackgroundTasksAdapter.upsertOne,

    /**
     * Accepts an array of sdbBackgroundTask entities and adds or updates them in the store.
     */
    upsertMany: sdbBackgroundTasksAdapter.upsertMany,

    /**
     * Accepts a single sdbBackgroundTask entity and removes it from the store.
     */
    removeOne: sdbBackgroundTasksAdapter.removeOne,

    /**
     * Accepts an array of sdbBackgroundTask entities and removes them from the store.
     */
    removeMany: sdbBackgroundTasksAdapter.removeMany,

    /**
     * Removes all the sdbBackgroundTask entities from the store.
     */
    removeAll: sdbBackgroundTasksAdapter.removeAll,

    /**
     * Resets the sdbBackgroundTask store state to the initial value.
     */
    resetSdbBackgroundTasksState: () => initialState,

    /**
     * Sets the page token for the page before the first fetched one
     */
    setBeforePage: (state, action: PayloadAction<StringEntityId | null>) => {
      state.pagination.before = action.payload;
    },

    /** Sets the page token for the page after the last fetched one */
    setAfterPage: (state, action: PayloadAction<StringEntityId | null>) => {
      state.pagination.after = action.payload;
    },

    /**
     * Sets the current page of the background tasks
     */
    setTotalBackgroundTasksCount: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.pagination.totalCount = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(updateBackgroundTasks.pending, (state) => {
        state.fetching.backgroundTasksFetchingStatus = "pending";
      })
      .addCase(updateBackgroundTasks.fulfilled, (state, action) => {
        state.fetching.backgroundTasksFetchingStatus = "succeeded";
        sdbBackgroundTasksAdapter.upsertMany(
          state,
          action.payload.sdbBackgroundTasks
        );
        state.pagination.totalCount = action.payload.totalCount;
        if (
          !state.pagination.before ||
          action.payload.shouldOverrideBeforePage
        ) {
          state.pagination.before = action.payload.before;
        }
        state.pagination.after = action.payload.after;
      })
      .addCase(updateBackgroundTasks.rejected, (state) => {
        state.fetching.backgroundTasksFetchingStatus = "rejected";
      })

      .addCase(fetchTasksByType.pending, (state, action) => {
        state.fetching.backgroundTasksFetchingStatus = "pending";
        // Track fetching status by task type to prevent race conditions
        // Only set per-type status when a specific type was requested
        const { taskType } = action.meta.arg;
        if (taskType) {
          state.fetching.fetchingStatusByType[taskType] = "pending";
        }
      })
      .addCase(fetchTasksByType.fulfilled, (state, action) => {
        state.fetching.backgroundTasksFetchingStatus = "succeeded";
        sdbBackgroundTasksAdapter.upsertMany(
          state,
          action.payload.sdbBackgroundTasks
        );
        // Track fetching status and count by task type to prevent race conditions
        const { taskType, totalCount } = action.payload;
        if (taskType) {
          // Per-type fetch: update the type-specific status and count
          state.fetching.fetchingStatusByType[taskType] = "succeeded";
          state.pagination.totalCountByType[taskType] = totalCount;
        } else {
          // All-types fetch: update the global total count
          state.pagination.totalCount = totalCount;
        }
      })
      .addCase(fetchTasksByType.rejected, (state, action) => {
        state.fetching.backgroundTasksFetchingStatus = "rejected";
        // Track fetching status by task type to prevent race conditions
        // Only set per-type status when a specific type was requested
        const { taskType } = action.meta.arg;
        if (taskType) {
          state.fetching.fetchingStatusByType[taskType] = "rejected";
        }
      });
  },
});

export const {
  upsertOne,
  upsertMany,
  removeOne,
  removeMany,
  removeAll,
  resetSdbBackgroundTasksState,
  setBeforePage,
  setAfterPage,
  setTotalBackgroundTasksCount,
} = backgroundTasksSlice.actions;

export const backgroundTasksReducer = backgroundTasksSlice.reducer;
